import { z } from "zod";

export * from "./redaction.js";
export * from "./verify.js";
export * from "./recurring.js";
export * from "./triage.js";

/**
 * The normalized shape every statement is converted into, whatever the bank.
 * Claude's only job on ingestion is to produce this; everything downstream is
 * deterministic code that runs on it.
 */

export const AccountKind = z.enum(["credit_card", "checking", "savings", "other"]);
export type AccountKind = z.infer<typeof AccountKind>;

export const TransactionKind = z.enum([
  "purchase",
  "payment", // payment to the card, or a transfer; excluded from spend analysis
  "credit", // refund or merchant credit
  "fee",
  "interest",
  "transfer",
  "other",
]);
export type TransactionKind = z.infer<typeof TransactionKind>;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");

/** Amounts are integer cents so arithmetic is exact. Positive = money out. */
const cents = z.number().int();

export const NormalizedTransaction = z
  .object({
    date: isoDate,
    /** Posting date when the statement lists one separately. */
    postedDate: isoDate.optional(),
    /** The merchant line exactly as printed, before any normalization. */
    rawDescription: z.string().min(1),
    amountCents: cents,
    kind: TransactionKind,
  })
  .strict();
export type NormalizedTransaction = z.infer<typeof NormalizedTransaction>;

export const NormalizedStatement = z
  .object({
    issuer: z.string().min(1),
    accountKind: AccountKind,
    /** Stable token the scrubber substituted for the real account number, e.g. "CARD-A". */
    accountToken: z.string().min(1),
    periodStart: isoDate,
    periodEnd: isoDate,
    /** Totals as printed on the statement, used to reconcile the rows. */
    printedTotals: z
      .object({
        purchasesCents: cents.optional(),
        paymentsAndCreditsCents: cents.optional(),
        feesCents: cents.optional(),
        interestCents: cents.optional(),
        newBalanceCents: cents.optional(),
      })
      .strict(),
    transactions: z.array(NormalizedTransaction),
  })
  .strict();
export type NormalizedStatement = z.infer<typeof NormalizedStatement>;

export interface ReconciliationResult {
  ok: boolean;
  problems: string[];
}

/**
 * Deterministic check that the extracted rows add up to what the statement
 * printed. Any mismatch blocks the statement until the user confirms it.
 */
const dollars = (cents: number) => `${cents < 0 ? "-" : ""}$${(Math.abs(cents) / 100).toFixed(2)}`;

export function reconcile(statement: NormalizedStatement): ReconciliationResult {
  const problems: string[] = [];
  const sum = (kinds: TransactionKind[]) =>
    statement.transactions
      .filter((t) => kinds.includes(t.kind))
      .reduce((acc, t) => acc + t.amountCents, 0);

  const { printedTotals } = statement;
  if (printedTotals.purchasesCents !== undefined) {
    const actual = sum(["purchase"]);
    if (actual !== printedTotals.purchasesCents) {
      problems.push(`Purchases: the rows add up to ${dollars(actual)} but the statement prints ${dollars(printedTotals.purchasesCents)}. A row is probably missing, duplicated or misread.`);
    }
  }
  if (printedTotals.paymentsAndCreditsCents !== undefined) {
    const actual = sum(["payment", "credit"]);
    if (actual !== printedTotals.paymentsAndCreditsCents) {
      problems.push(`Payments and credits: the rows add up to ${dollars(actual)} but the statement prints ${dollars(printedTotals.paymentsAndCreditsCents)}. A row is probably missing, duplicated or misread.`);
    }
  }
  if (printedTotals.feesCents !== undefined && sum(["fee"]) !== printedTotals.feesCents) {
    problems.push(`Fees: the rows add up to ${dollars(sum(["fee"]))} but the statement prints ${dollars(printedTotals.feesCents)}.`);
  }
  if (printedTotals.interestCents !== undefined && sum(["interest"]) !== printedTotals.interestCents) {
    problems.push(`Interest: the rows add up to ${dollars(sum(["interest"]))} but the statement prints ${dollars(printedTotals.interestCents)}.`);
  }
  // Transactions dated outside the printed period are kept as they are: issuers
  // routinely list charges posted just before the period, and every row is a
  // possible recurring charge. Only the printed totals are checked.
  return { ok: problems.length === 0, problems };
}

// --- Records exchanged between the API and the web app ----------------------

export const StatementStatus = z.enum([
  "queued", // uploaded, waiting for extraction
  "extracting", // Claude is reading it
  "needs_review", // extracted, but a check failed; waiting for the user
  "confirmed", // every check passed, or the user confirmed; rows count toward analysis
  "failed", // could not be read; see `error`
]);
export type StatementStatus = z.infer<typeof StatementStatus>;

export const StatementSummary = z.object({
  id: z.string(),
  fileName: z.string(),
  status: StatementStatus,
  uploadedAt: z.string(),
  issuer: z.string().optional(),
  accountToken: z.string().optional(),
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  transactionCount: z.number().int().optional(),
  problemCount: z.number().int().optional(),
  /** Confirmed by the ingest worker because every check passed, not by the user. */
  autoConfirmed: z.boolean().optional(),
  error: z.string().optional(),
});
export type StatementSummary = z.infer<typeof StatementSummary>;

/** What the web app sends to register an upload it has already redacted and put in S3. */
export const RegisterStatementRequest = z
  .object({
    key: z.string().min(1),
    fileName: z.string().min(1),
    /** SHA-256 of the redacted text, used to skip duplicate statements. */
    contentHash: z.string().length(64),
    /** The redacted text layer, used for the verbatim guard. */
    redactedText: z.string().min(1),
    tokens: z.record(z.string(), z.string()),
    pageCount: z.number().int().positive(),
  })
  .strict();
export type RegisterStatementRequest = z.infer<typeof RegisterStatementRequest>;

/** What the user confirms: the (possibly edited) statement. */
export const ConfirmStatementRequest = z.object({ statement: NormalizedStatement }).strict();
export type ConfirmStatementRequest = z.infer<typeof ConfirmStatementRequest>;
